import React, {
	ChangeEvent,
	memo,
	useCallback,
	useContext,
	useRef,
	useState,
} from "react";
import { APIProvider } from "@vis.gl/react-google-maps";
import fp from "lodash/fp";
import { Col, Form, Row } from "react-bootstrap";
import { AsyncTypeahead, RenderMenuProps } from "react-bootstrap-typeahead";
import Typeahead from "react-bootstrap-typeahead/types/core/Typeahead";
import {
	Option,
	TypeaheadManagerChildProps,
} from "react-bootstrap-typeahead/types/types";
import { useFormContext } from "react-hook-form";
import { WrappedFieldInputProps } from "redux-form";

import {
	BrandingColorContext,
	Button,
	RequiredMark,
	Skeleton,
} from "components";
import { GoogleAddressOptionFields, ValidationMessages } from "globalConstants";
import { isEmptyObject } from "helpers";
import { useGoogleGeocodeService } from "hooks";
import { validateAddressComponents } from "validators";

import EmptyLabel from "./emptyLabel";
import HintCustom from "./hintCustom";
import MenuCustom from "./menuCustom";

import { ReactComponent as RemoveIcon } from "static/images/cancel.svg";
import styles from "./addressSearchInput.module.scss";

type ReduxFormProps = {
	input?: WrappedFieldInputProps;
};

export type AddressSearchInputProps = {
	name: string;
	inputRef?: ((instance: HTMLInputElement | null) => void) | null;
	hookFormValue?: string | undefined;
	meta?: {
		touched: boolean;
		invalid: boolean;
		error: string;
		warning: string;
		asyncValidating: boolean;
	};
	minLength?: number;
	delay?: number;
	label?: string;
	labelHtml?: React.ReactNode;
	wrapperClassName?: string;
	labelClassName?: string;
	inputClassName?: string;
	smLabel?: number;
	smInput?: number;
	placeholder?: string;
	defaultInputValue?: string;
	isLoading?: boolean;
	isDisabled?: boolean;
	showRequiredMark?: boolean;
	onSelect?: (addressDetails: { [key: string]: any }) => void;
	onInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onFocus?: (e?: React.SyntheticEvent<HTMLInputElement>) => void;
	onBlur?: (e?: React.FocusEvent<HTMLInputElement, Element>) => void;
	onMenuToggle?: (isOpen?: boolean) => void;
	hasBrandingColor?: boolean;
	manualModeController?: (isManualMode: boolean) => void;
};

type AddressSearchInputCombinedProps = AddressSearchInputProps & ReduxFormProps;

const GOOGLE_MAP_API = process.env.REACT_APP_GOOGLE_MAP_API;

const { AddressNotVerified } = ValidationMessages;

const AddressSearch = ({
	name,
	input,
	inputRef,
	hookFormValue,
	meta,
	minLength = 3,
	delay = 500,
	label,
	labelHtml,
	wrapperClassName = "",
	labelClassName = "",
	inputClassName = "",
	smLabel = 6,
	smInput = 6,
	placeholder = "Enter address",
	defaultInputValue = "",
	isLoading = false,
	isDisabled = false,
	showRequiredMark = false,
	onSelect,
	onInputChange,
	onFocus,
	onBlur,
	onMenuToggle,
	hasBrandingColor = false,
	manualModeController,
}: AddressSearchInputCombinedProps) => {
	const divRef = useRef<HTMLDivElement | null>(null);
	const typeheadRef = useRef<Typeahead>(null);

	const {
		formState: { errors },
		clearErrors,
	}: any = useFormContext() || { formState: { errors: {} } };

	const hookFormErrorMsg = fp.get(name, errors)?.message;

	const [selectedValue, setSelectedValue] = useState<{ [key: string]: any }>(
		{},
	);
	const [predictionResults, setPredictionResults] = useState<
		{ [key: string]: any }[]
	>([]);
	const [addressErrorMsg, setAddressErrorMsg] = useState<string | null>(null);
	const [isManualMode, setIsManualMode] = useState<boolean>(false);

	const fieldName = input?.name || name;

	const isInvalid =
		(meta?.touched && meta?.invalid) ||
		!!addressErrorMsg ||
		!!hookFormErrorMsg ||
		false;
	const isError =
		(meta?.touched && !!meta?.error) ||
		!!addressErrorMsg ||
		!!hookFormErrorMsg ||
		false;
	const isWarning = (meta?.touched && !!meta?.warning) || false;

	const {
		fetchPredictions,
		getAddressDetails,
		isLoading: isDataLoading,
	} = useGoogleGeocodeService({ htmlDivElement: divRef?.current });

	const validateAddress = addressDetails => {
		if (isEmptyObject(addressDetails) || !addressDetails) return;

		if (
			!addressDetails[GoogleAddressOptionFields.Lat] ||
			!addressDetails[GoogleAddressOptionFields.Lng]
		) {
			setAddressErrorMsg?.(AddressNotVerified);
			return;
		}

		setAddressErrorMsg(
			validateAddressComponents(
				addressDetails[GoogleAddressOptionFields.Address],
			),
		);
	};

	const switchManualMode = useCallback(
		isManualMode => {
			setIsManualMode(isManualMode);
			manualModeController?.(isManualMode);
			if (isManualMode) {
				setAddressErrorMsg?.(null);
				// for react-hook-form - reset validation onClick "Enter Manually" btn
				clearErrors?.(fieldName);
			}
		},
		[clearErrors, fieldName, manualModeController],
	);

	const onChangeHandler = useCallback(
		(selected: Option[]) => {
			const placeId = selected?.[0]?.[GoogleAddressOptionFields.PlaceId];
			getAddressDetails(placeId, addressDetails => {
				switchManualMode(false);
				setPredictionResults([]);
				setSelectedValue(addressDetails);
				validateAddress(addressDetails);
				onSelect?.(addressDetails);
			});
		},
		[getAddressDetails, onSelect, switchManualMode],
	);

	const onSearchHandler = useCallback(
		(address: string) => {
			fetchPredictions(address, setPredictionResults, () => {
				setPredictionResults([]);
			});
		},
		[fetchPredictions],
	);

	const onInputChangeHandler = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			// Fix caret jumping to the end
			const selectionStart = e.target.selectionStart;

			setAddressErrorMsg?.(null);
			setSelectedValue({});
			input?.onChange(e.target.value);
			onInputChange?.(e);

			setTimeout(() => {
				// code review
				if (selectionStart !== null) {
					e.target.setSelectionRange(selectionStart, selectionStart);
				}
			}, 0);
		},
		[input, onInputChange],
	);

	const onBlurHandler = useCallback(
		(e: React.FocusEvent<HTMLInputElement, Element>) => {
			if (fp.isEmpty(selectedValue)) {
				validateAddress({});
			}
			input?.onBlur(e);
			onBlur?.(e);
		},
		[input, onBlur, selectedValue],
	);

	const onFocusHandler = useCallback(
		(e: React.SyntheticEvent<HTMLInputElement>) => {
			onFocus?.(e);
			input?.onFocus(e as React.FocusEvent<HTMLInputElement, Element>);
		},
		[input, onFocus],
	);

	const onMenuToggleHandler = useCallback(
		(isOpen: boolean) => {
			onMenuToggle?.(isOpen);
		},
		[onMenuToggle],
	);

	const onCleanHandler = useCallback(() => {
		typeheadRef.current?.clear();
		setAddressErrorMsg?.(null);
		setSelectedValue?.({ label: "" });
		setPredictionResults?.([]);
		onSelect?.({});
	}, [onSelect]);

	const brandingColor = useContext(BrandingColorContext) || hasBrandingColor;

	const brandingColorClass = brandingColor ? styles.brandingColor : "";
	const disabledClass = isDisabled ? styles.isDisabled : "";

	return (
		<Form.Group as={Row} className={wrapperClassName}>
			{(label || labelHtml) && (
				<Form.Label
					className={`${labelClassName} ${disabledClass}`}
					column
					onClick={e => e.preventDefault()}
					sm={smLabel}
					id={name}
				>
					{labelHtml || label}
					{showRequiredMark && !isDisabled && <RequiredMark />}
				</Form.Label>
			)}

			<Col
				sm={smInput}
				className={`${styles.container} ${inputClassName}`}
			>
				{isLoading && (
					<Skeleton height="34px" className={styles.skeleton} />
				)}
				<AsyncTypeahead
					id={fieldName}
					minLength={minLength}
					delay={delay}
					className={`${styles.typehead} ${brandingColorClass} ${disabledClass}`}
					isLoading={isDataLoading}
					onSearch={onSearchHandler}
					options={predictionResults}
					placeholder={placeholder}
					disabled={isDisabled}
					flip
					defaultInputValue={
						input?.value || hookFormValue || defaultInputValue
					}
					filterBy={(option, props) => {
						// Disable filtering of BE's data
						return true;
					}}
					ref={typeheadRef}
					useCache={false}
					renderInput={({
						inputRef: fieldRef,
						referenceElementRef,
						value,
						...inputProps
					}) => (
						<HintCustom className={styles.hintContainer}>
							{(input?.value || hookFormValue) && (
								<Button
									className={styles.btnClean}
									onClick={onCleanHandler}
									size="sm"
									icon={<RemoveIcon />}
									btnIcon
									variant="secondary"
									title="Clean"
									value={undefined}
									shape={undefined}
									minWidth={undefined}
									href={undefined}
									target={undefined}
									as={undefined}
									tabIndex={undefined}
									style={undefined}
									children={undefined}
									to={undefined}
									form={undefined}
								/>
							)}

							<Form.Control
								{...inputProps}
								value={
									input?.value ||
									hookFormValue ||
									(selectedValue.label ?? (value as string))
								}
								name={fieldName}
								aria-labelledby={fieldName}
								type="text"
								ref={input => {
									fieldRef(input);
									referenceElementRef(input);
									inputRef?.(input);
								}}
								isInvalid={isInvalid}
								onChange={(
									e: ChangeEvent<HTMLInputElement>,
								) => {
									onInputChangeHandler(e);
									inputProps?.onChange?.(e);
								}}
							/>
						</HintCustom>
					)}
					renderMenu={(
						results: Option[],
						menuProps: RenderMenuProps,
						props: TypeaheadManagerChildProps,
					) => (
						<MenuCustom
							{...menuProps}
							labelKey={props.labelKey}
							options={results}
							text={props.text}
							emptyLabel={
								<EmptyLabel
									showEnterManuallyBtn
									isManualMode={isManualMode}
									onClick={switchManualMode}
								/>
							}
						/>
					)}
					onChange={onChangeHandler}
					onBlur={onBlurHandler}
					onFocus={onFocusHandler}
					onMenuToggle={onMenuToggleHandler}
				/>
				{!isDisabled && (
					<>
						{isError && (
							<Form.Control.Feedback
								type="invalid"
								className="d-block"
							>
								{addressErrorMsg ||
									meta?.error ||
									hookFormErrorMsg}
							</Form.Control.Feedback>
						)}
						{!isError && isWarning && (
							<Form.Control.Feedback className="d-block text-warning">
								{meta?.warning}
							</Form.Control.Feedback>
						)}
					</>
				)}
				{
					// Div placeholder to pass to the PlacesService
				}
				<div ref={divRef}></div>
			</Col>
		</Form.Group>
	);
};

const AddressSearchInput = (props: AddressSearchInputProps) => {
	return (
		<>
			{GOOGLE_MAP_API ? (
				<APIProvider apiKey={GOOGLE_MAP_API}>
					<AddressSearch {...props} />
				</APIProvider>
			) : null}
		</>
	);
};

export default memo(AddressSearchInput);
